import React, { useMemo, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DeleteBlackIcon,
  EyIcon
} from '@shared/components/icons/svg-icon.tsx';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';
import TimeEstimateInput from '@shared/components/time-estimate-input/time-estimate-input.tsx';
import { PlaylistScreenRowProps } from '@modules/digital-signage/model/playlist-screen-row.model.ts';
import { PlaylistAnimationEnum } from '@modules/digital-signage/model/playlists.model.ts';
import { useTranslation } from 'react-i18next';
import AnimationPreviewPopover from '../animation-preview-popover/animation-preview-popover.tsx';

const PlaylistScreenRow: React.FC<PlaylistScreenRowProps> = ({
  fieldId,
  index,
  screens,
  onRemove
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const [previewAnchorEl, setPreviewAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const animationValue = useWatch({
    control,
    name: `screens.${index}.animation`,
    defaultValue: PlaylistAnimationEnum.None
  }) as PlaylistAnimationEnum;

  const screenValue = useWatch({
    control,
    name: `screens.${index}.screen`
  });

  const selectedScreenList = useMemo(() => {
    return screenValue ? [screenValue] : [];
  }, [screenValue]);
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging
  } = useSortable({ id: fieldId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    zIndex: isDragging ? 10 : 'auto',
    opacity: isDragging ? 0.5 : 1
  };

  const stopPropagation = (e: React.PointerEvent) => e.stopPropagation();

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2.5 p-2.5 rounded-xl border border-(--blue-12) cursor-grab active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-(--primary-100) text-(--primary) text-sm font-semibold shrink-0">
        {index + 1}
      </div>

      <div
        className="flex flex-1 items-start gap-2.5 min-w-0"
        onPointerDown={stopPropagation}
      >
        <div className="flex-1 min-w-0">
          <AppAutocompleteV2
            name={`screens.${index}.screen`}
            control={control}
            label=""
            options={screens}
            placeholder={t('placeholder_screen')}
            selectedList={selectedScreenList}
          />
        </div>

        <div className="w-42 shrink-0 max-lg-xl:w-32">
          <TimeEstimateInput
            name={`screens.${index}.visibleDuration`}
            label=""
            placeholder={t('placeholder_playlist_duration')}
            control={control}
          />
        </div>

        <div className="flex items-center gap-2.5 w-58 shrink-0 max-lg-xl:w-52">
          <FormControl fullWidth>
            <FormLabel>{''}</FormLabel>
            <Controller
              control={control}
              name={`screens.${index}.animation`}
              defaultValue={PlaylistAnimationEnum.None}
              render={({ field }) => (
                <Select {...field} input={<OutlinedInput />}>
                  <MenuItem value={PlaylistAnimationEnum.None}>
                    No Animation
                  </MenuItem>
                  <MenuItem value={PlaylistAnimationEnum.Fade}>Fade</MenuItem>
                </Select>
              )}
            />
          </FormControl>

          <IconButton
            size="small"
            onPointerDown={stopPropagation}
            onClick={(e) => setPreviewAnchorEl(e.currentTarget)}
            className="shrink-0 bg-(--gray-11)!"
          >
            <EyIcon fontSize="medium" className="opacity-80!" />
          </IconButton>
        </div>

        <AnimationPreviewPopover
          anchorEl={previewAnchorEl}
          animation={animationValue}
          onClose={() => setPreviewAnchorEl(null)}
        />
      </div>

      <IconButton
        onClick={() => onRemove(index)}
        color="error"
        className="shrink-0 bg-(--gray-11)!"
        onPointerDown={stopPropagation}
      >
        <DeleteBlackIcon />
      </IconButton>
    </div>
  );
};

export default PlaylistScreenRow;
